import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { t } from "./coachTokens";
import { weightGoalDirInfo, seriesDelta, deltaDirOk } from "./weightHelpers";
import { getTraineeProfile } from "../../../services/CoachDashboardService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const MON_TO_NUM = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

const HEB_MONTHS = {
  Jan: "ינו׳", Feb: "פבר׳", Mar: "מרץ", Apr: "אפר׳", May: "מאי", Jun: "יוני",
  Jul: "יולי", Aug: "אוג׳", Sep: "ספט׳", Oct: "אוק׳", Nov: "נוב׳", Dec: "דצמ׳",
};

export const parseLabel = (label) => {
  if (!label || typeof label !== "string") return null;
  const withDay = label.match(/^([A-Za-z]{3})\s+(\d{1,2}),?\s+(\d{4})$/);
  if (withDay) {
    const [, mon, day, year] = withDay;
    return { mon, day: parseInt(day, 10), year: parseInt(year, 10), monthOnly: false };
  }
  const monthOnly = label.match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (monthOnly) {
    const [, mon, year] = monthOnly;
    return { mon, day: null, year: parseInt(year, 10), monthOnly: true };
  }
  return null;
};

export const toShortNumericDate = (label) => {
  const p = parseLabel(label);
  if (!p) return label;
  const monNum = MON_TO_NUM[p.mon];
  if (!monNum) return label;
  if (p.monthOnly) return `${monNum}/${String(p.year).slice(2)}`;
  return `${p.day}/${monNum}`;
};

export const toHebDate = (label) => {
  const p = parseLabel(label);
  if (!p) return label;
  const heb = HEB_MONTHS[p.mon] || p.mon;
  if (p.monthOnly) return `${heb} ${p.year}`;
  return `${p.day} ${heb} ${p.year}`;
};

const RANGES = [
  { key: "month", label: "חודש" },
  { key: "3m", label: "3 חודשים" },
  { key: "6m", label: "6 חודשים" },
  { key: "year", label: "שנה" },
];

const RangePill = ({ active, label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      padding: "3px 9px",
      borderRadius: "20px",
      border: `1px solid ${active ? t.green : t.border}`,
      background: active ? t.green : t.white,
      color: active ? t.white : t.t2,
      fontSize: 10.5,
      fontWeight: active ? 700 : 500,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all .13s",
      letterSpacing: 0,
      textTransform: "none",
      "&:hover": active
        ? {}
        : { borderColor: t.t3, color: t.text },
    }}
  >
    {label}
  </Box>
);

const SectionRow = ({ delta, deltaColor, range, onRangeChange }) => (
  <Box
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      color: t.t3,
      textTransform: "uppercase",
      letterSpacing: ".08em",
      margin: "16px 0 9px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    }}
  >
    גרף משקל
    {delta != null && (
      <Box
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          color: deltaColor,
          background: `${deltaColor}15`,
          border: `1px solid ${deltaColor}40`,
          padding: "2px 8px",
          borderRadius: "10px",
          letterSpacing: 0,
          textTransform: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <Box component="span" sx={{ color: t.t3, fontWeight: 600 }}>סה״כ</Box>
        {delta > 0 ? "↑" : delta < 0 ? "↓" : "•"} {Math.abs(delta).toFixed(1)} ק"ג
      </Box>
    )}
    <Box sx={{ flex: 1, height: "1px", background: t.border }} />
    {onRangeChange && (
      <Box sx={{ display: "flex", gap: "4px" }}>
        {RANGES.map((r) => (
          <RangePill
            key={r.key}
            active={range === r.key}
            label={r.label}
            onClick={() => onRangeChange(r.key)}
          />
        ))}
      </Box>
    )}
  </Box>
);

const EmptyState = ({ label }) => (
  <Box
    sx={{
      background: t.bg,
      border: `1px dashed ${t.border}`,
      borderRadius: t.rs,
      padding: "14px",
      textAlign: "center",
      color: t.t3,
      fontSize: 12,
      marginBottom: "14px",
    }}
  >
    {label}
  </Box>
);

export const trendLineDataset = (series) => {
  const n = series.length;
  if (n < 2) return null;
  const xs = series.map((_, i) => i);
  const ys = series.map((p) => p.value);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - meanX) * (ys[i] - meanY);
    den += (xs[i] - meanX) * (xs[i] - meanX);
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  return xs.map((x) => +(intercept + slope * x).toFixed(2));
};

const WeightChart = ({ detail, traineeId, weekOffset = 0 }) => {
  const [range, setRange] = useState("month");
  const [overrideSeries, setOverrideSeries] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (range === "month" || traineeId == null) {
      setOverrideSeries(null);
      return;
    }
    let cancelled = false;
    setFetching(true);
    getTraineeProfile(traineeId, weekOffset, range)
      .then((res) => {
        if (cancelled) return;
        setOverrideSeries(res?.data?.weightSeries || []);
      })
      .catch(() => {
        if (!cancelled) setOverrideSeries([]);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, traineeId, weekOffset]);

  const series = overrideSeries != null ? overrideSeries : detail?.weightSeries;
  const hasData = Array.isArray(series) && series.length >= 2;

  const chart = useMemo(() => {
    if (!hasData) return null;
    const labels = series.map((p) => toShortNumericDate(p.label));
    const values = series.map((p) => p.value);
    const trend = trendLineDataset(series);

    const data = {
      labels,
      datasets: [
        {
          label: "משקל",
          data: values,
          borderColor: t.green,
          backgroundColor: `${t.green}22`,
          borderWidth: 2.5,
          pointRadius: 3.5,
          pointHoverRadius: 5,
          pointBackgroundColor: t.green,
          pointBorderColor: t.white,
          pointBorderWidth: 1.5,
          tension: 0.35,
          fill: true,
        },
        ...(trend
          ? [
              {
                label: "מגמה",
                data: trend,
                borderColor: "rgba(100,100,100,0.4)",
                borderDash: [5, 4],
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0,
                fill: false,
              },
            ]
          : []),
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: t.white,
          titleColor: t.t2,
          bodyColor: t.text,
          borderColor: t.border,
          borderWidth: 1,
          padding: 8,
          cornerRadius: 8,
          displayColors: false,
          titleFont: { size: 10, weight: "600" },
          bodyFont: { size: 11, weight: "700" },
          callbacks: {
            title: (items) => {
              if (!items || items.length === 0) return "";
              const idx = items[0].dataIndex;
              return toHebDate(series[idx]?.label);
            },
            label: (ctx) => {
              if (ctx.dataset.label === "מגמה") return null;
              return `${ctx.dataset.label}: ${(+ctx.parsed.y).toFixed(2)} ק"ג`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            font: { size: 10 },
            color: t.t3,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
          },
        },
        y: {
          grid: { color: "rgba(0,0,0,.04)" },
          border: { display: false },
          ticks: {
            font: { size: 10 },
            color: t.t3,
            padding: 8,
            callback: (v) => `${(+v).toFixed(1)} ק"ג`,
          },
        },
      },
    };

    return { data, options };
  }, [series, hasData]);

  if (!hasData) {
    return (
      <>
        <SectionRow range={range} onRangeChange={setRange} />
        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "30px", marginBottom: "14px" }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <EmptyState label="לא מספיק שקילות לבניית גרף" />
        )}
      </>
    );
  }

  const goal = weightGoalDirInfo(detail);
  const delta = seriesDelta(series);
  const dirOk = deltaDirOk(delta, goal?.dir);
  const deltaColor = dirOk == null ? t.t2 : dirOk ? t.green : t.red;

  return (
    <>
      <SectionRow
        delta={delta}
        deltaColor={deltaColor}
        range={range}
        onRangeChange={setRange}
      />
      <Box
        sx={{
          background: t.white,
          border: `1px solid ${t.border}`,
          borderRadius: t.rs,
          padding: "14px",
          marginBottom: "14px",
          boxShadow: t.sh,
          position: "relative",
        }}
      >
        <Box sx={{ height: 180, direction: "ltr", opacity: fetching ? 0.5 : 1, transition: "opacity .15s" }}>
          <Line data={chart.data} options={chart.options} />
        </Box>
        {fetching && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default WeightChart;
